import { createContext, useContext, useMemo, type CSSProperties, type HTMLAttributes } from 'react';
import { createConversationPalette, type ConversationPalette } from '../../lib/conversation-theme';
import './ConversationTheme.css';

const PaletteContext = createContext<ConversationPalette | null>(null);

/** Let canvas effects use the same local palette as CSS-based controls. */
export function useConversationPalette(): ConversationPalette | null {
  return useContext(PaletteContext);
}

/** A page shell that applies space branding only to its respondent subtree. */
interface ConversationThemeProps extends HTMLAttributes<HTMLDivElement> {
  accentColor?: string | null;
}

/** Preserve the app theme outside this page and fall back when no valid accent exists. */
export function ConversationTheme({ accentColor, children, style, className = '', ...props }: ConversationThemeProps) {
  const palette = useMemo(() => createConversationPalette(accentColor), [accentColor]);
  const colors: CSSProperties = palette ? {
    '--volt': palette.button,
    '--volt-dark': palette.hover,
    '--volt-soft': palette.soft,
    '--volt-rgb': [1, 3, 5].map(offset => parseInt(palette.accent.slice(offset, offset + 2), 16)).join(' '),
    '--accent': palette.button,
    '--accent-hover': palette.hover,
    '--accent-foreground': palette.foreground,
    '--conversation-on-accent': palette.foreground,
    '--conversation-text-light': palette.textOnLight,
    '--wash-volt': palette.wash,
    '--conversation-wash-hover': palette.washHover,
  } as CSSProperties : {};
  if (palette) {
    const roles: Record<string, string> = {};
    for (const [mode, button, choice] of [
      ['light', { background: palette.button, foreground: palette.foreground, hover: palette.hover, hoverForeground: palette.hoverForeground, active: palette.active, activeForeground: palette.activeForeground }, palette.choicesLight],
      ['dark', palette.darkButton, palette.choicesDark],
    ] as const) {
      for (const [role, values] of [['button', button], ['choice', choice]] as const) {
        for (const [state, value] of Object.entries(values)) {
          if (state === 'accent') continue;
          const suffix = state === 'background' ? '' : `-${state.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)}`;
          roles[`--conversation-${role}-${mode}${suffix}`] = value;
        }
      }
    }
    Object.assign(colors, roles);
  }
  return <PaletteContext.Provider value={palette}>
    <div {...props} data-conversation-theme={palette ? '' : undefined} className={`conversation-theme ${className}`} style={{ ...colors, ...style }}>
      {children}
    </div>
  </PaletteContext.Provider>;
}
